import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import db from './firebase.config.js';
import firebase from 'firebase/app';
import "firebase/firestore";
import './main.css';
import { genreDistribution, normalizeGenres, SUPPORTED_GENRES } from './genreDistribution';

// Genre color mapping
const GENRE_COLORS = {
  "hip hop": { bg: "rgba(168,130,255,0.10)", text: "#a882ff", border: "rgba(168,130,255,0.18)" },
  "rap": { bg: "rgba(168,130,255,0.10)", text: "#a882ff", border: "rgba(168,130,255,0.18)" },
  "rock": { bg: "rgba(255,140,105,0.10)", text: "#ff8c69", border: "rgba(255,140,105,0.18)" },
  "pop": { bg: "rgba(105,210,255,0.10)", text: "#69d2ff", border: "rgba(105,210,255,0.18)" },
  "r&b": { bg: "rgba(255,175,120,0.10)", text: "#ffaf78", border: "rgba(255,175,120,0.18)" },
  "edm": { bg: "rgba(120,255,200,0.10)", text: "#78ffc8", border: "rgba(120,255,200,0.18)" },
  "indie": { bg: "rgba(212,168,255,0.10)", text: "#d4a8ff", border: "rgba(212,168,255,0.18)" },
  "classical": { bg: "rgba(255,215,0,0.10)", text: "#ffd700", border: "rgba(255,215,0,0.18)" },
  "country": { bg: "rgba(200,168,92,0.10)", text: "#c8a85c", border: "rgba(200,168,92,0.18)" },
};

const DEFAULT_COLOR = { bg: "rgba(136,136,136,0.10)", text: "#888", border: "rgba(136,136,136,0.18)" };

const matchesSelection = (pending, live, pax) => live !== pending.snapshot &&
  Array.isArray(live?.[pax]?.pax_preferences) &&
  JSON.stringify(normalizeGenres(live[pax].pax_preferences)) === JSON.stringify(pending.preferences);

const GraphModal = ({ live_pax, uid, pax, sesh_id }) => {
  const dispatch = useDispatch();
  const pref_genres = useSelector(state => state.preferences);
  const [all_genres, setAllGenres] = useState(SUPPORTED_GENRES);
  const [checked, setChecked] = useState(() => normalizeGenres(pref_genres));
  const pendingSelection = useRef(null);
  const latestLive = useRef(live_pax);
  latestLive.current = live_pax;
  const [saveError, setSaveError] = useState('');

  const deepDocExists = async (docName, docId, docName2, docId2) => (await db.collection(docName).doc(docId).collection(docName2).doc(docId2).get()).exists;

  function sendPreferences(prefs){
    if(uid && uid !== ""){
      return db.collection('Users').doc(uid).update({
          ['live_pax_coords.'+pax+'.pax_preferences'] : prefs,
      }, {merge: true})
    }
  }

  async function getDriverPrefs(uid) {
    try {
      const driver_genres_ref = db.collection('Users').doc(uid)
      const id_snap = await driver_genres_ref.get();
      const supported = normalizeGenres(id_snap.data()?.genreInterest);
      const driver_genres = supported.length ? supported : SUPPORTED_GENRES
      setAllGenres(driver_genres)
      dispatch({type: 'Driver_Prefs', payload: {genres: driver_genres}})
    } catch (e) { console.log(e) }
  }

  useEffect(() => {
    if(uid && uid !== ""){
      getDriverPrefs(uid)
    }
    return () => { console.log("unmounted genres") }
  }, [uid])

  useEffect(() => {
    const pending = pendingSelection.current;
    if (pending) {
      // Ignore stale self data while still incorporating fresh votes from peers.
      // An optimistic snapshot alone cannot acknowledge a failed write.
      if (pending.saved && matchesSelection(pending, live_pax, pax)) {
        pendingSelection.current = null;
        setChecked(pending.preferences);
      }
      return;
    }
    setChecked(normalizeGenres(pref_genres, all_genres));
  }, [pref_genres, live_pax, pax, all_genres]);

  const handleToggle = (genre) => {
    const current = normalizeGenres(checked, all_genres);
    const currentIndex = current.indexOf(genre);
    let newChecked = [...current];

    if (currentIndex === -1) {
      newChecked.push(genre);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    newChecked = normalizeGenres(newChecked, all_genres);
    const pending = { preferences: newChecked, snapshot: live_pax, saved: false };
    pendingSelection.current = pending;
    setSaveError('');
    setChecked(newChecked);
    dispatch({type: 'Pax_Prefs', payload: {preferences: newChecked}});
    dispatch({type: 'Pax_Prefs_Changed', payload: {preferences_change: true}});

    // Update Firestore
    if(pax && sesh_id) {
      const pax_sesh_doc_ref = db.collection('Passengers').doc(pax).collection("sessions").doc(sesh_id)
      const pax_sesh_exists = deepDocExists("Passengers", pax, "sessions", sesh_id)
      try{
        pax_sesh_exists.then((check_val) => {
          if(check_val){
            var unix_rn = Date.now() / 1000
            db.collection('Passengers').doc(pax).update({
              'last_used' : firebase.firestore.FieldValue.serverTimestamp(),
            })
            pax_sesh_doc_ref.update({
              'actions':firebase.firestore.FieldValue.arrayUnion('genres_requested'),
              'interactions': firebase.firestore.FieldValue.arrayUnion({
                'action' : 'genres_requested',
                'meta' : newChecked,
                'time' : unix_rn
              }),
            })
          }
        })
      }catch (e){ console.log(e) }
    }
    Promise.resolve(sendPreferences(newChecked)).then(() => {
      pending.saved = true;
      if (pendingSelection.current === pending && matchesSelection(pending, latestLive.current, pax)) {
        pendingSelection.current = null;
      }
    }).catch(() => {
      if (pendingSelection.current === pending) {
        pendingSelection.current = null;
        setSaveError('Couldn’t save your genres. Try again.');
      }
    });
  };

  const { rows, total } = genreDistribution({ genres: all_genres, selected: checked, live: live_pax, self: pax });

  return (
    <div className='pulse-card'>
      <div className='pulse-header'>
        <div className='pulse-dot' />
        <span className='pulse-label'>The ride’s taste</span>
        <span className='pulse-votes'>{total ? `${total} selection${total === 1 ? '' : 's'}` : 'No selections'}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {rows.map(({ genre, percent: pct }) => {
          const colors = GENRE_COLORS[genre] || DEFAULT_COLOR;
          const isActive = checked.includes(genre);

          return (
            <button
              key={genre}
              className={`pulse-genre-row ${isActive ? 'active' : 'inactive'}`}
              onClick={() => handleToggle(genre)}
              aria-pressed={isActive}
              style={{
                background: isActive ? colors.bg : 'transparent',
                borderColor: isActive ? colors.border : 'transparent',
              }}
            >
              <span className='pulse-genre-name' style={{ color: isActive ? colors.text : 'rgba(240,240,242,0.35)' }}>
                {genre}
              </span>

              <div className='pulse-bar-track'>
                <div
                  className='pulse-bar-fill'
                  style={{
                    width: `${pct}%`,
                    background: isActive
                      ? `linear-gradient(90deg, ${colors.text}, ${colors.text}88)`
                      : 'rgba(255,255,255,0.12)',
                  }}
                />
              </div>

              <span className='pulse-pct' style={{ color: isActive ? colors.text : 'rgba(240,240,242,0.35)' }}>
                {pct}%
              </span>
            </button>
          );
        })}
      </div>
      {saveError && <p className="error-text" role="alert">{saveError}</p>}
    </div>
  );
};

export default GraphModal;
